import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useCmsProjectItems } from '../hooks/useCmsProjectItems';
import { ProjectCard } from './ProjectCard';

import 'swiper/css';
import 'swiper/css/pagination';

const HOME_MAX_PROJECTS = 10;

/**
 * Ana sayfa proje şeridi — `GET /cms/Project/GetAll?pageNumber=1&pageSize=10`.
 * Giriş yapılmışsa isteğe `Authorization: Bearer` eklenir.
 */
export function Projects() {
    const { t } = useTranslation();
    const items = useCmsProjectItems({ maxItems: HOME_MAX_PROJECTS });
    const swiperRef = useRef(null);
    const enableLoop = (items?.length ?? 0) > 3;
    const [nav, setNav] = useState({ prev: false, next: true });

    const updateNav = useCallback(
        (swiper) => {
            if (!swiper) return;
            if (swiper.isLocked) {
                setNav({ prev: false, next: false });
                return;
            }
            if (enableLoop) {
                setNav({ prev: true, next: true });
                return;
            }
            setNav({
                prev: !swiper.isBeginning,
                next: !swiper.isEnd,
            });
        },
        [enableLoop]
    );

    const body =
        items == null ? (
            <p
                className="text-center text-secondary mb-0 py-5"
                role="status"
                aria-live="polite"
            >
                {t('home.projects.loading')}
            </p>
        ) : items.length === 0 ? (
            <p className="text-center text-secondary mb-0 py-5">
                {t('home.projects.empty')}
            </p>
        ) : (
            <div className="position-relative card-swiper-shell px-xl-4">
                <button
                    type="button"
                    className="card-swiper-nav card-swiper-nav-prev"
                    disabled={!nav.prev}
                    aria-label={t('home.projects.prevNav')}
                    onClick={() => swiperRef.current?.slidePrev()}
                >
                    <ChevronLeft size={28} strokeWidth={1.65} />
                </button>
                <button
                    type="button"
                    className="card-swiper-nav card-swiper-nav-next"
                    disabled={!nav.next}
                    aria-label={t('home.projects.nextNav')}
                    onClick={() => swiperRef.current?.slideNext()}
                >
                    <ChevronRight size={28} strokeWidth={1.65} />
                </button>
                <Swiper
                    className="projects-swiper pb-5"
                    modules={[Pagination]}
                    slidesPerView={1}
                    spaceBetween={16}
                    loop={enableLoop}
                    pagination={{ clickable: true }}
                    watchOverflow
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                        updateNav(swiper);
                    }}
                    onSlideChange={updateNav}
                    onBreakpoint={(swiper) => updateNav(swiper)}
                    breakpoints={{
                        768: {
                            slidesPerView: 2,
                            spaceBetween: 20,
                        },
                        992: {
                            slidesPerView: 3,
                            spaceBetween: 24,
                        },
                    }}
                >
                    {items.map((item) => (
                        <SwiperSlide key={item.id}>
                            <ProjectCard item={item} />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        );

    return (
        <section
            className="projects-section bg-light"
            aria-labelledby="projects-baslik"
            aria-busy={items == null ? 'true' : undefined}
        >
            <div className="container">
                <h2
                    id="projects-baslik"
                    className="projects-section-title text-center mb-4 mb-lg-5"
                >
                    {t('home.projects.title')}
                </h2>
                {body}
                <div className="text-center mt-4 pt-1">
                    <Link
                        to="/projects"
                        className="d-inline-block rounded-pill section-see-all-btn text-decoration-none"
                    >
                        {t('home.projects.seeAll')}
                    </Link>
                </div>
            </div>
        </section>
    );
}
